<template>
  <DefaultLayout :disableFacilitatorTools="true">
    <div ref="topdiv">
      <h5>{{ $t("createnew") }}</h5>
      <br />

      <div v-if="!newGameCreated">
        <small class="text-uppercase font-weight-bold">{{ $t("entername") }}</small>
        <base-input
          maxLength="50"
          ref="gamename"
          v-focus
          id="input-gamename"
          v-model="form.gameName"
        />
        <div v-if="$store.state.main.limited_facilitator_allowed_create_game_scenario_id !== null && $store.state.main.limited_facilitator_allowed_create_game_rps_scenario_id !== null">
          <br/>
          <small class="text-uppercase font-weight-bold">{{ $t("selecttype") }}</small>
          <div class="form-check">
            <input class="form-check-input" type="radio" name="gameType" id="regularGame" value="regular" v-model="form.gameType">
            <label class="form-check-label" for="regularGame">
              {{ $t("regulargame") }}
            </label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="radio" name="gameType" id="rpsGame" value="rps" v-model="form.gameType">
            <label class="form-check-label" for="rpsGame">
              {{ $t("rpsgame") }}
            </label>
          </div>
        </div>
      </div>

      <div class="alert alert-primary" role="alert" v-if="alertMessage">
        {{ $t(alertMessage) }}
      </div>
    </div>

    <div v-if="newGameCreated">
      <PlayerObserverLinks
        :gameCode="form.gameCode"
        :showObserverLink="$store.state.main.userViewObserverLink"
      />
    </div>
    <br />
    <div class="d-flex">
      <base-button
        v-if="!newGameCreated"
        type="primary"
        class="ml-auto"
        :disabled="!form.gameName"
        @click="submit()"
      >
        {{ $t("save") }}
      </base-button>
      <base-button
        v-if="!newGameCreated"
        type="link"
        class="ml-auto"
        @click="cancel()"
      >
        {{ $t("cancel") }}
      </base-button>
      <base-button
        v-if="newGameCreated"
        type="primary"
        class="ml-auto"
        @click="closeDialog()"
      >
        {{ $t("close") }}
      </base-button>
    </div>
  </DefaultLayout>
</template>

<script>
import { createGame } from "@/services/APIService";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import PlayerObserverLinks from "@/components/PlayerObserverLinks";
import { generateRobots } from "@/services/robot.js";

export default {
  components: {
    DefaultLayout,
    PlayerObserverLinks
  },
  data() {
    return {
      form: {
        gameName: "",
        gameCode: "",
        locale: this.$i18n.locale,
        gameType: this.$store.state.main.limited_facilitator_allowed_create_game_scenario_id !== null ? "classic" : "rockpaperscissors"
      },
      alertMessage: null,
      newGameCreated: false
    };
  },
  methods: {
    async submit() {
      if (!this.form.gameName) {
        this.alertMessage = "allfieldsrequired";
        return;
      }

      const isClassic = this.form.gameType === "classic";

      try {
        const response = await createGame(
          this.form.gameName,
          "",
          this.$store.state.main.selectedLocale,
          this.$store.state.main.limited_facilitator_allowed_create_game_scenario_id,
          this.$store.state.main.limited_facilitator_allowed_create_game_rps_scenario_id,
          false,
          "",
          true,
          !!isClassic,
          7,
          false,
          false,
          this.form.gameType,
          true
        );

        this.form.gameCode = response.game_code;
        this.newGameCreated = true;
        this.alertMessage = null;
      } catch (error) {
        console.error(error);
        this.alertMessage = "error";
      }
    },
    cancel() {
      this.$router.push("/admin/games");
    },
    closeDialog() {
      this.$router.push("/admin/games");
    }
  }
};
</script>

<i18n>
{
  "en": {
    "entername": "* Name of the simulation, team or occasion (visible to players)",
    "createnew": "Create new simulation",
    "cancel": "Cancel",
    "start": "Start",
    "allfieldsrequired": "Please fill in all fields before proceeding",
    "error": "An error occurred",
    "selecttype": "* Select game type",
    "regulargame": "Regular game",
    "rpsgame": "RPS game",
    "save": "Save",
    "close": "Close"
  },
  "fi": {
    "entername": "* Simulaation, tiimin tai tilaisuuden nimi (näkyy pelaajille)",
    "createnew": "Luo uusi simulaatio",
    "cancel": "Peru",
    "start": "Aloita",
    "allfieldsrequired": "Täytä kaikki vaaditut kentät",
    "error": "Virhe tapahtui",
    "selecttype": "* Valitse pelin tyyppi",
    "regulargame": "Tavallinen peli",
    "rpsgame": "RPS-peli",
    "save": "Tallenna",
    "close": "Sulje"
  },
  "de": {
    "entername": "* Name der Simulation, des Teams oder des Anlasses (für Spieler sichtbar)",
    "createnew": "Neue Simulation erstellen",
    "cancel": "Abbrechen",
    "start": "Start",
    "allfieldsrequired": "Bitte füllen Sie alle Felder aus, bevor Sie fortfahren",
    "error": "Ein Fehler ist aufgetreten",
    "selecttype": "* Wählen Sie den Spieltyp",
    "regulargame": "Reguläres Spiel",
    "rpsgame": "RPS-Spiel",
    "save": "Speichern",
    "close": "Schließen"
  },
  "sv": {
    "entername": "* Simuleringens, teamets eller evenemangets namn (synligt för spelare)",
    "createnew": "Skapa en ny simulering",
    "cancel": "Ångra",
    "start": "Start",
    "allfieldsrequired": "Vänligen fyll i alla fält innan du fortsätter",
    "error": "Ett fel har inträffat",
    "selecttype": "* Välj speltyp",
    "regulargame": "Vanligt spel",
    "rpsgame": "RPS-spel",
    "save": "Spara",
    "close": "Stäng"
  },
  "ko": {
    "entername": "* 시뮬레이션 이름, 그룹/팀 이름 등 (전체공개)",
    "createnew": "신규 시뮬레이션 개설",
    "cancel": "취소",
    "start": "시작",
    "allfieldsrequired": "모든 정보를 입력해주십시오",
    "error": "오류가 발생했습니다",
    "selecttype": "* 게임 유형 선택",
    "regulargame": "일반 게임",
    "rpsgame": "RPS 게임",
    "save": "저장",
    "close": "닫기"
  }
}
</i18n>
